// BookFreely SEO helper — sets/updates document <head> tags per route.
//
// Public routes should set:
//   - title, meta description
//   - canonical URL
//   - og:url, og:title, og:description
//   - robots: "index,follow"
//
// Private / auth-gated routes (login/signup, cart, dashboards, admin) should
// set robots: "noindex,follow" so they never surface in search.
//
// Kept dependency-free (no react-helmet). Only touches head tags we own,
// leaves the rest untouched so the base tags from index.html keep working.

import { useEffect } from "react";

const SITE_ORIGIN = "https://bookfreely.co";
const DEFAULT_DESCRIPTION =
  "BookFreely is an online marketplace that helps customers discover and book independent local service businesses with secure online payments.";

function upsertMeta(attr, name, content) {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Set SEO tags for the current route.
 * @param {object} opts
 * @param {string} [opts.title]            Page <title>
 * @param {string} [opts.description]      Meta description
 * @param {string} [opts.path]             Route path (used to build canonical / og:url)
 * @param {boolean} [opts.noindex=false]   If true, sets robots="noindex,follow"
 */
export function useSeo({ title, description, path, noindex = false } = {}) {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const desc = description || DEFAULT_DESCRIPTION;
    const routePath = path || (typeof window !== "undefined" ? window.location.pathname : "/");
    // Strip query and hash so canonical/og:url is stable for the same route
    const cleanPath = routePath.split("?")[0].split("#")[0] || "/";
    const url = SITE_ORIGIN + cleanPath;

    if (title) document.title = title;
    upsertMeta("name", "description", desc);
    upsertMeta("name", "robots", noindex ? "noindex,follow" : "index,follow");
    upsertLink("canonical", url);
    upsertMeta("property", "og:url", url);
    if (title) upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", desc);
    upsertMeta("name", "twitter:title", title || "BookFreely");
    upsertMeta("name", "twitter:description", desc);
  }, [title, description, path, noindex]);
}
